import React, { useEffect, useState, useCallback } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
    Users, UserPlus, Trash2, MapPin,
    Calendar, Heart, Baby, Globe, ChevronDown,
    Save, HelpCircle, AlertCircle, CheckCircle, Clock, Edit2
} from "lucide-react";

// Create fresh copy of empty sibling
const createFreshSibling = (createEmptySibling) => {
    const newSibling = createEmptySibling();
    return { ...newSibling };
};

const SiblingForm = ({ siblings, setSiblings, createEmptySibling, previewMode, handleChange, preview, setPreview, loading, onSave }) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    const STATUS_CONFIG = {
        pending: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending Verification" },
        approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <CheckCircle size={14} />, label: "Verified & Approved" },
        reject: { color: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Information Rejected" },
    };

    // Ensure siblings is always an array
    const safeSiblings = Array.isArray(siblings) ? siblings : [];

    // Initialize expanded state for sibling cards
    useEffect(() => {
        const initialExpandedState = {};
        safeSiblings.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedCards(initialExpandedState);
    }, [safeSiblings.length]);

    const toggleCard = (index) => {
        setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const addSibling = useCallback(() => {
        if (setSiblings && createEmptySibling) {
            setSiblings(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newSibling = createFreshSibling(createEmptySibling);
                const newArray = [...currentArray, newSibling];
                const newIndex = newArray.length - 1;

                setExpandedCards(prevExpanded => ({
                    ...prevExpanded,
                    [newIndex]: true
                }));

                return newArray;
            });
        }
    }, [setSiblings, createEmptySibling]);

    const removeSibling = useCallback((index) => {
        if (setSiblings && safeSiblings.length > 1) {
            setSiblings(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newArray = currentArray.filter((_, i) => i !== index);

                setExpandedCards(prevExpanded => {
                    const newExpanded = {};
                    Object.keys(prevExpanded).forEach(key => {
                        const numKey = parseInt(key);
                        if (numKey < index) {
                            newExpanded[numKey] = prevExpanded[numKey];
                        } else if (numKey > index) {
                            newExpanded[numKey - 1] = prevExpanded[numKey];
                        }
                    });
                    return newExpanded;
                });

                return newArray;
            });
        }
    }, [setSiblings, safeSiblings.length]);

    const handleChangeField = useCallback((index, field, value) => {
        if (handleChange) {
            handleChange(index, field, value);
        }
    }, [handleChange]);

    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    };

    const validateForm = () => {
        const newErrors = {};
        safeSiblings.forEach((sibling, i) => {
            if (!sibling.relation) newErrors[`relation_${i}`] = "Relationship is required";
            if (!sibling.name) newErrors[`name_${i}`] = "First name is required";
            if (!sibling.surname) newErrors[`surname_${i}`] = "Surname is required";
            if (!sibling.dateOfBirth) newErrors[`dateOfBirth_${i}`] = "Date of birth is required";
        });
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in preview mode
    if ((previewMode || preview) && !isEditing) {
        return (
            <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <Users size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Sibling Records</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Provide details of all your brothers and sisters</p>
                            </div>
                        </div>
                        {preview?.status && (
                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider ${STATUS_CONFIG[preview.status?.toLowerCase()]?.color || "bg-gray-100 text-gray-600"}`}>
                                {STATUS_CONFIG[preview.status?.toLowerCase()]?.icon}
                                <span>{STATUS_CONFIG[preview.status?.toLowerCase()]?.label || preview.status}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Sibling information verified and approved</p>
                        </div>
                    )}

                    <div className="space-y-4 sm:space-y-5">
                        {safeSiblings.filter(s => s.name).length === 0 ? (
                            <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                <Users size={40} className="text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">No sibling records added</p>
                            </div>
                        ) : (
                            safeSiblings.filter(s => s.name).map((s, i) => (
                                <div key={i} className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:shadow-md transition-all duration-300">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-sm sm:text-base capitalize">
                                            {s.relation}: {s.name} {s.surname}
                                        </h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                        <PreviewItem icon={<UserPlus size={16} />} label="Full Name" value={`${s.name} ${s.surname}`} />
                                        <PreviewItem icon={<Heart size={16} />} label="Marital Status" value={s.maritalStatus} capitalize />
                                        <PreviewItem icon={<Calendar size={16} />} label="Date of Birth" value={formatDate(s.dateOfBirth)} />
                                        <PreviewItem icon={<Globe size={16} />} label="Birth Place" value={s.placeOfBirth} />
                                        <PreviewItem icon={<MapPin size={16} />} label="In Canada" value={s.isInCanada ? "Yes" : "No"} highlight={s.isInCanada} />
                                        <PreviewItem icon={<Baby size={16} />} label="Status" value={s.dateOfDeath ? "Deceased" : "Alive"} />
                                        <div className="sm:col-span-2 lg:col-span-3">
                                            <PreviewItem icon={<MapPin size={16} />} label="Address" value={s.currentAddress} />
                                        </div>
                                        {s.isInCanada && (
                                            <div className="sm:col-span-2 lg:col-span-3 bg-blue-50 p-3 rounded-xl">
                                                <PreviewItem icon={<Globe size={16} />} label="Canada Address" value={s.canadaAddress} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {(preview?.status === "reject" || !preview?.status) && (
                        <div className="flex justify-center pt-6 mt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
                            >
                                <Edit2 size={16} /> Edit Sibling Information
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving sibling information...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <Users size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Sibling Records</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Add information about your brothers and sisters</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {(Object.keys(localErrors).length > 0) && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {safeSiblings.length === 0 ? (
                        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <Users size={40} className="text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">No sibling records added</p>
                            <button
                                type="button"
                                onClick={addSibling}
                                className="mt-3 text-blue-600 text-sm font-medium flex items-center gap-1 mx-auto"
                            >
                                <UserPlus size={16} /> Add your first sibling
                            </button>
                        </div>
                    ) : (
                        safeSiblings.map((item, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                                {/* Card Header - Clickable on mobile */}
                                <div
                                    className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gray-50/50"
                                    onClick={() => toggleCard(index)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base capitalize">
                                                {item.relation ? `${item.relation}` : "New Sibling"}
                                            </h3>
                                            {item.name && (
                                                <p className="text-[10px] sm:text-xs text-gray-500">
                                                    {item.name} {item.surname}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            className="md:hidden text-gray-400"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCard(index);
                                            }}
                                        >
                                            <ChevronDown size={18} className={`transition-transform ${expandedCards[index] ? 'rotate-180' : ''}`} />
                                        </button>
                                        {safeSiblings.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeSibling(index);
                                                }}
                                                className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className={`
                                    transition-all duration-300 overflow-hidden
                                    ${expandedCards[index] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                                `}>
                                    <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                            <SelectField
                                                label="Relationship"
                                                value={item.relation}
                                                onChange={(v) => handleChangeField(index, "relation", v)}
                                                options={["Brother", "Sister"]}
                                                error={localErrors[`relation_${index}`]}
                                                required
                                            />
                                            <SelectField
                                                label="Marital Status"
                                                value={item.maritalStatus}
                                                onChange={(v) => handleChangeField(index, "maritalStatus", v)}
                                                options={["Single", "Married", "Common-law", "Divorced", "Widowed", "Separated"]}
                                                error={localErrors[`maritalStatus_${index}`]}
                                            />

                                            <InputField
                                                label="First Name"
                                                value={item.name}
                                                onChange={(v) => handleChangeField(index, "name", v)}
                                                error={localErrors[`name_${index}`]}
                                                required
                                            />
                                            <InputField
                                                label="Surname"
                                                value={item.surname}
                                                onChange={(v) => handleChangeField(index, "surname", v)}
                                                error={localErrors[`surname_${index}`]}
                                                required
                                            />

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
                                                    Date of Birth <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={item.dateOfBirth || ""}
                                                    onChange={(e) => handleChangeField(index, "dateOfBirth", e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`dateOfBirth_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`dateOfBirth_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`dateOfBirth_${index}`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">Date of Death</label>
                                                <input
                                                    type="date"
                                                    value={item.dateOfDeath || ""}
                                                    onChange={(e) => handleChangeField(index, "dateOfDeath", e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    min={item.dateOfBirth}
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm"
                                                />
                                            </div>

                                            <InputField
                                                label="Place of Birth"
                                                value={item.placeOfBirth}
                                                onChange={(v) => handleChangeField(index, "placeOfBirth", v)}
                                                placeholder="City, Country"
                                            />

                                            <div className="md:col-span-2">
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">Current Residential Address</label>
                                                <textarea
                                                    rows="2"
                                                    value={item.currentAddress}
                                                    onChange={(e) => handleChangeField(index, "currentAddress", e.target.value)}
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm resize-none"
                                                    placeholder="Full address"
                                                />
                                            </div>

                                            <div className="md:col-span-2 p-3 sm:p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={item.isInCanada}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            handleChangeField(index, "isInCanada", checked);
                                                            if (!checked) handleChangeField(index, "canadaAddress", "");
                                                        }}
                                                    />
                                                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Presently living in Canada?</span>
                                                </label>

                                                {item.isInCanada && (
                                                    <div className="mt-3 animate-in slide-in-from-top-2">
                                                        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">Canada Residential Address</label>
                                                        <input
                                                            value={item.canadaAddress}
                                                            onChange={(e) => handleChangeField(index, "canadaAddress", e.target.value)}
                                                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm"
                                                            placeholder="Street, City, Province, Postal Code"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            type="button"
                            onClick={addSibling}
                            className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 py-3 rounded-xl font-semibold text-sm transition-all"
                        >
                            <UserPlus size={18} /> Add Another Sibling
                        </button>

                    </div>
                </form>
            </div>
        </div>
    );
};

/* ================= HELPERS ================= */
const InputField = ({ label, value, onChange, required, placeholder, error }) => (
    <div>
        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
            {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            placeholder={placeholder}
            className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                ${error ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
        />
        {error && <p className="text-rose-500 text-[10px] mt-1">{error}</p>}
    </div>
);

const SelectField = ({ label, value, onChange, options, required, error }) => (
    <div>
        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
            {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <div className="relative">
            <select
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all appearance-none cursor-pointer text-sm
                    ${error ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
            >
                <option value="">Select {label}</option>
                {options.map(opt => <option key={opt} value={opt.toLowerCase()}>{opt}</option>)}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
        {error && <p className="text-rose-500 text-[10px] mt-1">{error}</p>}
    </div>
);

const PreviewItem = ({ icon, label, value, highlight, capitalize }) => (
    <div className="flex items-start gap-2 sm:gap-3">
        <div className="mt-0.5 text-gray-400 shrink-0">{icon}</div>
        <div className="flex flex-col min-w-0 flex-1">
            <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5">{label}</span>
            <span className={`text-xs sm:text-sm font-semibold break-words leading-tight ${highlight ? 'text-blue-600' : 'text-gray-800'} ${capitalize ? 'capitalize' : ''}`}>
                {value || <span className="text-gray-300 font-normal italic">N/A</span>}
            </span>
        </div>
    </div>
);

export default SiblingForm;